// Parser and layout for the .bngraph file `bino build` writes next to each
// PDF: the artefact's dependency graph as a text/tabwriter table with the
// columns KIND, NAME, HASH, DEPENDS ON and DETAILS. This module has no imports,
// so the Node test runner can load it as it is.

export interface GraphNode {
  /** "Kind:Name", the label the DEPENDS ON column uses. */
  id: string;
  kind: string;
  name: string;
  hash: string;
  /** The DETAILS cell as written. */
  details: string;
  /** The labels this node depends on; one may name a node the file lacks. */
  deps: string[];
}

export interface Graph {
  nodes: GraphNode[];
  byId: Map<string, GraphNode>;
  /** Reverse edges: the ids that depend on the key. */
  usedBy: Map<string, string[]>;
}

const COLUMNS = ["KIND", "NAME", "HASH", "DEPENDS ON", "DETAILS"];

/** The kind of the artefact node, the root of every graph. */
export const ROOT_KIND = "ReportArtefact";

const KIND_ORDER = [ROOT_KIND, "LayoutPage", "LayoutCard", "Component", "DataSet", "DataSource"];

/**
 * parseBngraph reads the table, or returns null when the text is not one: the
 * format is made for people and may change, and the caller then shows it as
 * plain text.
 */
export function parseBngraph(text: string): Graph | null {
  const lines = text.split(/\r?\n/).filter((l) => l.trim() !== "");
  const header = lines.shift();
  if (header === undefined) return null;

  const offsets: number[] = [];
  let from = 0;
  for (const col of COLUMNS) {
    const at = header.indexOf(col, from);
    if (at < 0) return null;
    offsets.push(at);
    from = at + col.length;
  }
  if (offsets[0] !== 0) return null;

  const rows: string[][] = [];
  for (const line of lines) {
    // tabwriter pads by code point, not by UTF-16 unit.
    const chars = Array.from(line);
    for (const at of offsets.slice(1)) {
      if (chars.length > at && (chars[at - 1] !== " " || chars[at - 2] !== " ")) return null;
    }
    rows.push(
      offsets.map((at, i) =>
        chars
          .slice(at, offsets[i + 1])
          .join("")
          .trim(),
      ),
    );
  }

  const kinds = new Set(rows.map((r) => r[0]));
  const byId = new Map<string, GraphNode>();
  for (const [kind, name, hash, deps, details] of rows) {
    const id = `${kind}:${name}`;
    if (!kind || !name || byId.has(id)) continue;
    byId.set(id, { id, kind, name, hash, details, deps: splitDeps(deps, kinds) });
  }
  if (byId.size === 0) return null;

  const usedBy = new Map<string, string[]>();
  for (const n of byId.values()) {
    for (const dep of n.deps) {
      if (!byId.has(dep)) continue;
      const list = usedBy.get(dep);
      if (list) list.push(n.id);
      else usedBy.set(dep, [n.id]);
    }
  }
  return { nodes: [...byId.values()], byId, usedBy };
}

/**
 * splitDeps cuts the DEPENDS ON cell into labels. A name can hold ", " itself
 * ("page_01 (REGION: north, YEAR: 2025)"), so a piece starts a new label only
 * when it begins with a kind the file has.
 */
function splitDeps(cell: string, kinds: Set<string>): string[] {
  const out: string[] = [];
  if (!cell) return out;
  for (const piece of cell.split(", ")) {
    if (out.length > 0 && !kinds.has(piece.split(":", 1)[0])) {
      out[out.length - 1] += ", " + piece;
    } else {
      out.push(piece);
    }
  }
  return out;
}

/** closure returns id with everything it depends on and everything that uses it. */
export function closure(g: Graph, id: string): Set<string> {
  const reach = (next: (id: string) => string[]) => {
    const seen = new Set<string>([id]);
    const stack = [id];
    for (let cur = stack.pop(); cur !== undefined; cur = stack.pop()) {
      for (const n of next(cur)) {
        if (!g.byId.has(n) || seen.has(n)) continue;
        seen.add(n);
        stack.push(n);
      }
    }
    return seen;
  };
  return new Set([
    ...reach((cur) => g.byId.get(cur)?.deps ?? []),
    ...reach((cur) => g.usedBy.get(cur) ?? []),
  ]);
}

// Diagram geometry, in SVG user units.
export const NODE_W = 176;
export const NODE_H = 24;
const ROW = 28;
const COL_GAP = 88;
const PAD = 16;
const HEAD = 28;

export interface PlacedNode {
  node: GraphNode;
  x: number;
  y: number;
}

export interface PlacedEdge {
  from: string;
  to: string;
  /** SVG path data. */
  d: string;
  /** The edge starts at the artefact node. */
  fromRoot: boolean;
}

export interface Layout {
  width: number;
  height: number;
  /** x and y are the top left of the column's label row. */
  columns: { kind: string; x: number; y: number; count: number }[];
  nodes: PlacedNode[];
  edges: PlacedEdge[];
  /** Label rows above a column's nodes that only the artefact itself uses. */
  dividers: { x: number; y: number; count: number }[];
}

/**
 * layout places one column per kind, from the artefact on the left to the data
 * sources on the right, and routes an edge from each node to what it depends
 * on. With `only`, it places just those ids.
 */
export function layout(g: Graph, only?: Set<string>): Layout {
  const nodes = only ? g.nodes.filter((n) => only.has(n.id)) : g.nodes;
  const shown = new Set(nodes.map((n) => n.id));
  const rank = (kind: string) => {
    const i = KIND_ORDER.indexOf(kind);
    return i < 0 ? KIND_ORDER.length : i;
  };
  const kinds = [...new Set(nodes.map((n) => n.kind))].sort(
    (a, b) => rank(a) - rank(b) || a.localeCompare(b),
  );
  const colOf = new Map(kinds.map((k, i) => [k, i]));
  const parents = (n: GraphNode) =>
    (g.usedBy.get(n.id) ?? []).filter((p) => shown.has(p)).map((p) => g.byId.get(p)!);

  const out: Layout = { width: 0, height: 0, columns: [], nodes: [], edges: [], dividers: [] };
  const rowOf = new Map<string, number>();
  const placed = new Map<string, PlacedNode>();
  let maxRows = 0;

  kinds.forEach((kind, col) => {
    const x = PAD + col * (NODE_W + COL_GAP);
    const members = nodes.filter((n) => n.kind === kind);
    out.columns.push({ kind, x, y: PAD, count: members.length });

    // A node sits near the mean row of its parents in the columns before.
    const key = new Map<string, number>();
    const rootOnly = new Set<string>();
    for (const n of members) {
      const ps = parents(n);
      const rows = ps.filter((p) => colOf.get(p.kind)! < col).map((p) => rowOf.get(p.id)!);
      key.set(n.id, rows.length ? rows.reduce((a, b) => a + b, 0) / rows.length : Infinity);
      if (ps.length > 0 && ps.every((p) => p.kind === ROOT_KIND)) rootOnly.add(n.id);
    }
    // Split only a column that has both sorts; a column of pages is all root-only.
    const split = rootOnly.size > 0 && rootOnly.size < members.length;
    const tail = (n: GraphNode) => (split && rootOnly.has(n.id) ? 1 : 0);
    const sorted = [...members].sort(
      (a, b) =>
        tail(a) - tail(b) || key.get(a.id)! - key.get(b.id)! || a.name.localeCompare(b.name),
    );

    // A node used by one of its own column goes right below that node.
    const ordered: GraphNode[] = [];
    const done = new Set<string>();
    const emit = (n: GraphNode) => {
      if (done.has(n.id)) return;
      done.add(n.id);
      ordered.push(n);
      for (const c of sorted) {
        if (n.deps.includes(c.id)) emit(c);
      }
    };
    for (const n of sorted) {
      if (!parents(n).some((p) => p.kind === kind)) emit(n);
    }
    for (const n of sorted) emit(n); // what a cycle left out

    let row = 0;
    for (const n of ordered) {
      if (split && rootOnly.has(n.id) && !out.dividers.some((d) => d.x === x)) {
        out.dividers.push({ x, y: PAD + HEAD + row * ROW, count: rootOnly.size });
        row++;
      }
      rowOf.set(n.id, row);
      const p = { node: n, x, y: PAD + HEAD + row * ROW };
      placed.set(n.id, p);
      out.nodes.push(p);
      row++;
    }
    maxRows = Math.max(maxRows, row);
  });

  for (const from of out.nodes) {
    for (const dep of from.node.deps) {
      const to = placed.get(dep);
      if (!to) continue;
      out.edges.push({
        from: from.node.id,
        to: dep,
        d: edgePath(from, to),
        fromRoot: from.node.kind === ROOT_KIND,
      });
    }
  }

  out.width = 2 * PAD + kinds.length * NODE_W + Math.max(0, kinds.length - 1) * COL_GAP;
  out.height = 2 * PAD + HEAD + maxRows * ROW;
  return out;
}

function edgePath(a: PlacedNode, b: PlacedNode): string {
  const y1 = a.y + NODE_H / 2;
  const y2 = b.y + NODE_H / 2;
  if (a.x === b.x) {
    // Same column: an arc through the gap on the right.
    const x = a.x + NODE_W;
    const bulge = x + COL_GAP / 2;
    return `M${x},${y1} C${bulge},${y1} ${bulge},${y2} ${x},${y2}`;
  }
  const x1 = a.x < b.x ? a.x + NODE_W : a.x;
  const x2 = a.x < b.x ? b.x : b.x + NODE_W;
  const mid = (x1 + x2) / 2;
  return `M${x1},${y1} C${mid},${y1} ${mid},${y2} ${x2},${y2}`;
}
